'use client';

import { cn } from '@/lib/utils';
import { hrPrimaryButtonClass, hrSecondaryButtonClass } from '@/lib/hr-ui';
import {
  PwaPrimaryPillButton,
  PwaSecondaryPillButton,
  PwaTerminalPillButton,
} from './PwaPillButtons';

export type SheetDualFooterProps = {
  variant?: 'pill' | 'hr';
  /**
   * row — Cancel | Primary рядом (формы).
   * stack — Primary сверху, Secondary снизу full-width (confirm / promo sheet, Dodo-inspired).
   */
  layout?: 'row' | 'stack';
  /** Цвет primary в pill-режиме: emerald бренд или оранжевый terminal. */
  primaryTone?: 'primary' | 'terminal';
  cancelLabel?: string;
  primaryLabel: string;
  loadingLabel?: string;
  onCancel: () => void;
  onPrimary: () => void;
  cancelDisabled?: boolean;
  primaryDisabled?: boolean;
  loading?: boolean;
  primaryClassName?: string;
  className?: string;
};

export function SheetDualFooter({
  variant = 'pill',
  layout = 'row',
  primaryTone = 'primary',
  cancelLabel = 'Отмена',
  primaryLabel,
  loadingLabel,
  onCancel,
  onPrimary,
  cancelDisabled = false,
  primaryDisabled = false,
  loading = false,
  primaryClassName,
  className,
}: SheetDualFooterProps) {
  const disabled = loading || primaryDisabled;
  const label = loading && loadingLabel ? loadingLabel : primaryLabel;
  const Primary =
    primaryTone === 'terminal' ? PwaTerminalPillButton : PwaPrimaryPillButton;
  const pillCompact = 'min-w-0 flex-1 px-4 py-3 text-sm font-semibold';
  const handlePrimary = () => {
    if (disabled) return;
    onPrimary();
  };

  if (variant === 'hr') {
    return (
      <div className={cn(layout === 'stack' ? 'flex flex-col gap-2.5' : 'flex gap-2.5', className)}>
        {layout === 'stack' ? (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={handlePrimary}
              className={cn(hrPrimaryButtonClass, 'w-full min-h-[2.75rem]', primaryClassName)}
            >
              {label}
            </button>
            <button
              type="button"
              disabled={cancelDisabled || loading}
              onClick={onCancel}
              className={cn(hrSecondaryButtonClass, 'w-full min-h-[2.75rem]')}
            >
              {cancelLabel}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={cancelDisabled || loading}
              onClick={onCancel}
              className={cn(hrSecondaryButtonClass, 'min-h-[2.75rem] min-w-0 flex-1')}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={handlePrimary}
              className={cn(hrPrimaryButtonClass, 'min-h-[2.75rem] min-w-0 flex-1', primaryClassName)}
            >
              {label}
            </button>
          </>
        )}
      </div>
    );
  }

  if (layout === 'stack') {
    return (
      <div className={cn('flex flex-col gap-2.5', className)}>
        <Primary
          type="button"
          disabled={disabled}
          onClick={handlePrimary}
          className={cn('py-3 text-sm font-semibold shadow-md', primaryClassName)}
        >
          {label}
        </Primary>
        <PwaSecondaryPillButton
          type="button"
          disabled={cancelDisabled || loading}
          onClick={onCancel}
          className="py-3 text-sm font-semibold"
        >
          {cancelLabel}
        </PwaSecondaryPillButton>
      </div>
    );
  }

  return (
    <div className={cn('flex gap-2.5', className)}>
      <PwaSecondaryPillButton
        type="button"
        disabled={cancelDisabled || loading}
        onClick={onCancel}
        className={pillCompact}
      >
        {cancelLabel}
      </PwaSecondaryPillButton>
      <Primary
        type="button"
        disabled={disabled}
        onClick={handlePrimary}
        className={cn(pillCompact, 'shadow-md', primaryClassName)}
      >
        {label}
      </Primary>
    </div>
  );
}
